import { API_VERSION } from "@next-platform/client-common-core";
import { API_URL, WITH_CREDENTIALS } from "@next-platform/client-common-core/infra/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import type { UpdateUserRequest } from "@next-platform/packages-ts-types";
import { ClientHttpHeaders } from "@next-platform/packages-ts-types";
import type {
  AxiosErrorWithApiData,
  ConfirmEmailChangeResponse,
  GenerateImageArgs,
  RequestEmailChangeResponse,
  SaveImageArgs,
  SubmitFeedbackArgs,
} from "@src/types/api";
import axios from "axios";

import { JWT_LOCAL_STORAGE_KEY, WHITE_LABEL_API_VERSION } from "@/constants";
import { isConversationAccessDeniedError } from "@/utils";

import apiClient from "./apiClient";

const logger = appLogger.withTag("JsonApiService");

class JsonApiService {
  private guestUserId: string | null = null;
  private externalUserId: string | null = null;
  private applicationId: string | null = null;
  private useWithCredentials: boolean | string = WITH_CREDENTIALS as string;
  private baseUrl = API_URL;

  setGuestUserId(id: string | null) {
    this.guestUserId = id;
  }

  setExternalUserId(id: string | null) {
    this.externalUserId = id;
  }

  setApplicationId(applicationId: string) {
    this.applicationId = applicationId;
  }

  setBaseUrl(url: string) {
    this.baseUrl = url;
  }

  async fetchResource(resource: string, id: string | null = null, params: Record<string, string | number> = {}, version: API_VERSION | null = null) {
    try {
      const url = `${this.getBaseUrlVersioned(version)}/${resource}${id ? `/${id}` : ""}`;
      const response = await apiClient.get(url, { params });

      return response.data;
    } catch (error) {
      logger.error(`Error fetching resource:'${resource}': ${this.formatError(error)}`);
      throw error;
    }
  }

  async createResource(resource: string, data: Record<string, unknown>, version?: API_VERSION | null) {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned(version)}/${resource}`, data);
      return response.data;
    } catch (error) {
      logger.error(`Error creating resource '${resource}': ${this.formatError(error)}`);
      throw error;
    }
  }

  async updateResource(resource: string, id: string | null = null, data: Record<string, unknown>, version: API_VERSION | null = null) {
    try {
      const response = await apiClient.patch(`${this.getBaseUrlVersioned(version)}/${resource}${id ? `/${id}` : ""}`, data);
      return response.data;
    } catch (error) {
      logger.error(`Error updating resource '${resource}': ${this.formatError(error)}`);
      throw error;
    }
  }

  async deleteResource(resource: string, id: string | null = null, data?: Record<string, unknown>) {
    try {
      let url = `${this.getBaseUrlVersioned()}/${resource}`;
      if (id) {
        url = `${this.getBaseUrlVersioned()}/${resource}/${id}`;
      }
      return await apiClient.delete(url, { data });
    } catch (error) {
      logger.error(`Error deleting resource '${resource}': ${this.formatError(error)}`);
      throw error;
    }
  }

  async updateUser(data: UpdateUserRequest) {
    try {
      const response = await apiClient.patch(`${this.getBaseUrlVersioned()}/users`, data).catch((error) => {
        logger.error("error", error?.response?.data?.error);
        throw new Error(error?.response?.data?.error);
      });
      return response.data;
    } catch (error) {
      logger.error(`Error updating user: ${this.formatError(error)}`);
      throw error;
    }
  }

  /**
   * Starts an email change by sending a verification code to the new address. Nothing is written to
   * the account until `confirmEmailChange` succeeds. Re-requesting supersedes any code still in
   * flight, so this is also the "resend" path.
   *
   * Unlike `updateUser` above, the AxiosError is left to propagate: the server carries the case in
   * `errorCode`, and rethrowing as a plain Error would discard it before `useApi` can read it.
   */
  async requestEmailChange(email: string): Promise<RequestEmailChangeResponse> {
    const response = await apiClient.post(`${this.getBaseUrlVersioned()}/users/email-change/request`, { email });
    return response.data;
  }

  /**
   * Completes an email change. The pending request is resolved server-side from the authenticated
   * user, so the code is the only thing to send.
   */
  async confirmEmailChange(code: string): Promise<ConfirmEmailChangeResponse> {
    const response = await apiClient.post(`${this.getBaseUrlVersioned()}/users/email-change/confirm`, { code });
    return response.data;
  }

  async createUserExternal(name: string, username: string) {
    try {
      const payload = {
        id: this.externalUserId,
        name: name,
        username: username,
      };

      logger.info("creating external user");

      const jwt = new URLSearchParams(window.location.search).get(JWT_LOCAL_STORAGE_KEY);

      const response = await apiClient
        .post(`${this.getBaseUrlVersioned(WHITE_LABEL_API_VERSION as API_VERSION)}/users/external`, payload, {
          headers: { [ClientHttpHeaders.AUTHORIZATION]: `Bearer ${jwt}` },
          withCredentials: false,
        })
        .catch((error) => {
          logger.error("Error creating external user", error.response.data.error);
          throw new Error(error.response.data.error);
        });
      // We receive both user and JWT token in the response
      return response.data;
    } catch (error) {
      logger.error(`Error in creating external user: ${this.formatError(error)}`);
      throw error;
    }
  }

  async refreshJwt() {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned(WHITE_LABEL_API_VERSION as API_VERSION)}/auth/refresh`, null, {
        withCredentials: Boolean(this.useWithCredentials),
      });
      return response.data;
    } catch (error) {
      logger.error(`Error refreshing JWT: ${this.formatError(error)}`);
      throw error;
    }
  }

  async generateImage({ prompt, negative_prompt, seed, image, model_style, abortSignal }: GenerateImageArgs) {
    try {
      const response = await apiClient.post(
        `${this.getBaseUrlVersioned()}/generate-image`,
        {
          prompt,
          negative_prompt,
          seed,
          image,
          model_style,
        },
        { signal: abortSignal },
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        logger.info("[generateImage] Request canceled");
        throw new Error("AbortError");
      } else if ((error as AxiosErrorWithApiData)?.response?.status === 429) {
        throw error;
      }
      const errorMessage = (error as AxiosErrorWithApiData)?.response?.data?.error || (error as Error)?.message;
      logger.error(`Error when generating images: ${this.formatError(error)}`);
      throw new Error(errorMessage);
    }
  }
  async saveImage({ prompt, image_type, hash, fileSize, upload_type }: SaveImageArgs) {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned()}/save-image`, { prompt, image_type, hash, fileSize, upload_type });

      return response.data;
    } catch (error) {
      logger.error(`Error when saving image: ${this.formatError(error)}`);
      if ((error as AxiosErrorWithApiData)?.response?.status === 429) {
        throw error;
      }
      throw new Error((error as Error).message);
    }
  }

  async checkUsername(username: string) {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned()}/users/check-username`, { username });
      return response.data;
    } catch (error) {
      logger.error(`Error when checking username: ${this.formatError(error)}`);
      throw new Error((error as Error).message);
    }
  }

  // This is not used right now
  async updateChatMessage(chatmessageID: string, messages: string[]) {
    const data = { content: messages };
    try {
      const response = await apiClient.patch(`${this.getBaseUrlVersioned()}/messages/${chatmessageID}`, data);

      return response.data;
    } catch (error) {
      logger.error(`Error when updating chat message: ${this.formatError(error)}`);
      throw new Error((error as Error).message);
    }
  }

  async deleteChatConversation(conversationId: string, messages: string[]) {
    const payload = { ids: messages };
    try {
      const response = await apiClient.delete(`${this.getBaseUrlVersioned()}/conversations/${conversationId}/messages`, {
        data: payload,
      });
      return response.data;
    } catch (error) {
      logger.error(`Error when deleting chat conversation: ${this.formatError(error)}`);
      throw new Error((error as Error).message);
    }
  }

  async getCharacterMessages(characterId: string, conversationId: string | null = null, params: Record<string, string> = {}) {
    let url = `${this.getBaseUrlVersioned()}/characters/${characterId}/messages`;
    if (conversationId) {
      url = `${this.getBaseUrlVersioned()}/characters/${characterId}/messages/${conversationId}`;
    }
    if (params) {
      //we iterate through all keys to add query string, adding ? then &
      let query = "?";
      Object.keys(params).forEach((key) => {
        if (params[key]) {
          query += `${key}=${params[key]}&`;
        }
      });
      url += query;
    }

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      if (!isConversationAccessDeniedError(error)) {
        logger.error(`Error when getting character's messages: ${this.formatError(error)}`);
      }
      throw new Error((error as AxiosErrorWithApiData)?.response?.data?.error);
    }
  }

  async submitFeedback({ messageId, label }: SubmitFeedbackArgs) {
    const url = `${this.getBaseUrlVersioned()}/messages/${messageId}/label`;
    const data = { message_id: messageId, label };
    try {
      const response = await apiClient.post(url, data);
      return response.data;
    } catch (error) {
      logger.error(`Error when submitting feedback: ${this.formatError(error)}`);
      throw new Error((error as AxiosErrorWithApiData)?.response?.data?.error);
    }
  }

  /**
   * Suggests AI-generated content for character fields
   * @param {string} field - Field to generate content for ('persona', 'greeting', 'scenario', 'dialogue')
   * @param {Object} fieldsValues - Optional object with values of other known fields for context
   * @returns {Promise<Object>} Response containing the suggested content
   */
  async suggestCharacterField(field: string, fieldsValues: Record<string, string> = {}) {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned()}/characters/suggest_field`, {
        field,
        fields_values: fieldsValues,
      });
      return response.data;
    } catch (error) {
      logger.error(`Error suggesting character field '${field}': ${this.formatError(error)}`);
      throw new Error((error as AxiosErrorWithApiData)?.response?.data.error);
    }
  }

  /**
   * Suggests AI-generated tags for character based on character fields
   * @param {Object} characterFields - Object containing character fields (name, title, greeting, persona, scenario, dialogue)
   * @returns {Promise<Object>} Response containing the suggested tags
   */
  async suggestCharacterTags(characterFields: Record<string, unknown>) {
    try {
      const response = await apiClient.post(`${this.getBaseUrlVersioned()}/characters/suggest_tags`, characterFields);
      return response.data;
    } catch (error) {
      logger.error(`Error suggesting character tags: ${this.formatError(error)}`);
      throw new Error((error as AxiosErrorWithApiData)?.response?.data.error);
    }
  }

  getBaseUrlVersioned(version: API_VERSION | null = null): string | undefined {
    if (version === null || version === "v1") {
      return this.baseUrl;
    } else if (version) {
      return `${this.baseUrl}/${version}`;
    }
    return this.baseUrl;
  }

  formatError(error: AxiosErrorWithApiData | Error | unknown): string {
    if (axios.isAxiosError(error)) {
      return `${error.name} with '[${error.code}/${error.response?.status}]-${error.message} on ${error?.config?.url}`;
    }
    if (error instanceof Error) {
      return `${error.name} with '${error.message}'`;
    }
    return `Unknown error: ${JSON.stringify(error)}`;
  }
}

const jsonApiService = new JsonApiService();

export default jsonApiService;
